import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { STATE_CODES } from "@/lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, MapPin, Shield, Landmark, PenTool, FileText, Save, Loader2, Upload, Check, HardDrive, Unplug, RefreshCw, FolderSync, Globe, MessageCircle } from "lucide-react";
import { toast } from "sonner";

// E.164 international phone format — leading `+`, country code, then 7-14 digits.
const E164_REGEX = /^\+[1-9]\d{6,14}$/;

function maskE164(raw) {
  if (!raw) return "";
  const s = String(raw);
  const hasPlus = s.trim().startsWith("+");
  const digits = s.replace(/\D+/g, "");
  return hasPlus || digits.length > 0 ? `+${digits}` : "";
}

export default function BusinessSettingsPage() {
  const { user, setUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [form, setForm] = useState({
    business_name: "", business_address: "", city: "", state_code: "", pin: "",
    is_gst_registered: false, gstin: "",
    gst_status: "unregistered", business_sector: "", composition_category: "",
    bank_name: "", bank_account: "", bank_ifsc: "", bank_branch: "",
    signatory_name: "", invoice_template: "professional",
    logo_base64: "", signature_base64: "",
    export_type_preference: "", lut_number: "", lut_date: "",
    ad_code: "", refund_bank_account: "", default_export_currency: "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const logoRef = useRef(null);
  const sigRef = useRef(null);

  // Google Drive state
  const [driveStatus, setDriveStatus] = useState({ connected: false });
  const [driveLoading, setDriveLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [currencies, setCurrencies] = useState([]);

  // WhatsApp Business identity — E.164 phone number + verification state
  const [waNumber, setWaNumber] = useState("");
  const [waEditing, setWaEditing] = useState(false);
  const [waSaving, setWaSaving] = useState(false);
  const isWhatsAppVerified = !!user?.is_whatsapp_verified;
  const currentPhone = user?.phone_number || "";

  const fetchDriveStatus = async () => {
    try {
      const res = await api.get("/drive/status");
      setDriveStatus(res.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchDriveStatus();
    api.get("/currencies").then((r) => setCurrencies(r.data)).catch(() => {});
    const driveParam = searchParams.get("drive");
    if (driveParam === "connected") {
      toast.success("Google Drive connected successfully!");
      fetchDriveStatus();
      searchParams.delete("drive");
      setSearchParams(searchParams, { replace: true });
    } else if (driveParam === "error") {
      const reason = searchParams.get("reason");
      toast.error(
        reason
          ? `Failed to connect Google Drive: ${decodeURIComponent(reason)}`
          : "Failed to connect Google Drive. Please try again."
      );
      searchParams.delete("drive");
      searchParams.delete("reason");
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (user) {
      setForm((prev) => ({
        ...prev,
        business_name: user.business_name || "",
        business_address: user.business_address || "",
        city: user.city || "",
        state_code: user.state_code || "",
        pin: user.pin || "",
        is_gst_registered: user.is_gst_registered ?? false,
        gstin: user.gstin || "",
        gst_status: user.gst_status || "unregistered",
        business_sector: user.business_sector || "",
        composition_category: user.composition_category || "",
        bank_name: user.bank_name || "",
        bank_account: user.bank_account || "",
        bank_ifsc: user.bank_ifsc || "",
        bank_branch: user.bank_branch || "",
        signatory_name: user.signatory_name || "",
        invoice_template: user.invoice_template || "professional",
        logo_base64: user.logo_base64 || "",
        signature_base64: user.signature_base64 || "",
        export_type_preference: user.export_type_preference || "",
        lut_number: user.lut_number || "",
        lut_date: user.lut_date || "",
        ad_code: user.ad_code || "",
        refund_bank_account: user.refund_bank_account || "",
        default_export_currency: user.default_export_currency || "",
      }));
    }
  }, [user]);

  const update = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    setSaved(false);
  };

  const handleImageUpload = (field, ref) => {
    const file = ref.current?.files?.[0];
    if (!file) return;
    if (file.size > 500000) {
      toast.error("Image must be under 500KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => update(field, reader.result);
    reader.readAsDataURL(file);
  };

  // Keep the WhatsApp input mirror synced with the user object whenever it updates
  useEffect(() => {
    setWaNumber(user?.phone_number || "");
  }, [user?.phone_number]);

  const saveWhatsApp = async () => {
    if (!waNumber || !E164_REGEX.test(waNumber)) {
      toast.error("Enter a valid E.164 number, e.g. +919812345678");
      return;
    }
    setWaSaving(true);
    try {
      const r = await api.post("/user/update-whatsapp", { phone_number: waNumber });
      setUser(r.data);
      setWaEditing(false);
      toast.success("WhatsApp number saved. Verification will be requested on first use.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save number");
    } finally {
      setWaSaving(false);
    }
  };

  const disconnectWhatsApp = async () => {
    setWaSaving(true);
    try {
      const r = await api.post("/user/update-whatsapp", { disconnect: true });
      setUser(r.data);
      setWaNumber("");
      setWaEditing(false);
      toast.success("WhatsApp disconnected");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Disconnect failed");
    } finally {
      setWaSaving(false);
    }
  };

  const handleSave = async () => {
    if (!form.business_name || !form.state_code) {
      toast.error("Business Name and State are required");
      return;
    }
    if (form.is_gst_registered && (!form.gstin || form.gstin.length !== 15)) {
      toast.error("Valid 15-character GSTIN required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.is_gst_registered) payload.gstin = "";
      const response = await api.put("/settings", payload);
      setUser(response.data);
      setSaved(true);
      toast.success("Settings saved successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const connectDrive = async () => {
    setDriveLoading(true);
    try {
      const res = await api.get("/drive/connect");
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to initiate Drive connection");
      setDriveLoading(false);
    }
  };

  const disconnectDrive = async () => {
    setDriveLoading(true);
    try {
      await api.post("/drive/disconnect");
      setDriveStatus({ connected: false });
      toast.success("Google Drive disconnected");
    } catch (err) {
      toast.error("Failed to disconnect");
    } finally {
      setDriveLoading(false);
    }
  };

  const syncDrive = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/drive/sync");
      toast.success(res.data.message);
      fetchDriveStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const selectedState = STATE_CODES.find((s) => s.code === form.state_code);

  return (
    <div className="p-6 md:p-8 max-w-3xl mx-auto space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-outfit text-2xl font-bold text-[#0f172a] tracking-tight">Business Settings</h1>
        <p className="text-sm text-[#64748b] mt-1">Configure your business details, bank info, and invoice preferences.</p>
      </div>

      {/* Business Details */}
      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="font-outfit text-base font-semibold text-[#0f172a] flex items-center gap-2">
            <Building2 className="h-4 w-4 text-[#4338ca]" /> Business Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm font-medium mb-1.5 block">Legal Business Name *</Label>
            <Input value={form.business_name} onChange={(e) => update("business_name", e.target.value)}
              data-testid="settings-business-name" className="h-10 border-[#e2e8f0]" />
          </div>
          <div>
            <Label className="text-sm font-medium mb-1.5 block">Registered Address</Label>
            <Input value={form.business_address} onChange={(e) => update("business_address", e.target.value)}
              placeholder="123 MG Road" data-testid="settings-address" className="h-10 border-[#e2e8f0]" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">City</Label>
              <Input value={form.city} onChange={(e) => update("city", e.target.value)}
                data-testid="settings-city" className="h-10 border-[#e2e8f0]" />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block flex items-center gap-1"><MapPin className="h-3 w-3" /> State *</Label>
              <Select value={form.state_code} onValueChange={(v) => update("state_code", v)}>
                <SelectTrigger data-testid="settings-state" className="h-10 border-[#e2e8f0]">
                  <SelectValue>{selectedState ? `${selectedState.code}-${selectedState.name}` : "Select"}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {STATE_CODES.map((s) => (
                    <SelectItem key={s.code} value={s.code}>{s.code}-{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">PIN</Label>
              <Input value={form.pin} onChange={(e) => update("pin", e.target.value)} maxLength={6}
                data-testid="settings-pin" className="h-10 border-[#e2e8f0] font-mono" />
            </div>
          </div>

          {/* Link WhatsApp Account — foundational WhatsApp Business API identity */}
          <div className="pt-2 mt-2 border-t border-[#f1f5f9]" data-testid="whatsapp-link-section">
            <Label className="text-sm font-medium mb-1.5 flex items-center gap-1.5">
              <MessageCircle className="h-3.5 w-3.5 text-[#25D366]" />
              Link WhatsApp Account
            </Label>

            {isWhatsAppVerified && !waEditing ? (
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[220px] flex items-center gap-2 h-10 px-3 rounded-lg bg-[#f1f5f9] border border-[#e2e8f0]">
                  <span className="font-mono text-sm text-[#0f172a]" data-testid="whatsapp-number-display">{currentPhone || "—"}</span>
                </div>
                <span
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#10b981]/10 text-[#10b981] border border-[#10b981]/20"
                  data-testid="whatsapp-connected-badge"
                >
                  <span className="inline-block h-2 w-2 rounded-full bg-[#10b981] animate-pulse" />
                  Connected to WhatsApp
                </span>
                <button
                  type="button"
                  onClick={() => setWaEditing(true)}
                  data-testid="whatsapp-change-btn"
                  className="text-xs text-[#4338ca] hover:text-[#3730a3] hover:underline"
                >
                  Change number
                </button>
                <button
                  type="button"
                  onClick={disconnectWhatsApp}
                  disabled={waSaving}
                  data-testid="whatsapp-disconnect-btn"
                  className="text-xs text-red-500 hover:text-red-600 hover:underline disabled:opacity-50"
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[220px]">
                  <Input
                    type="tel"
                    value={waNumber}
                    onChange={(e) => setWaNumber(maskE164(e.target.value))}
                    placeholder="+919812345678"
                    inputMode="tel"
                    autoComplete="tel"
                    pattern="^\+[1-9][0-9]{6,14}$"
                    data-testid="whatsapp-number-input"
                    className={`h-10 border-[#e2e8f0] font-mono tracking-wide ${waNumber && !E164_REGEX.test(waNumber) ? "border-red-400" : ""}`}
                  />
                </div>
                <Button
                  type="button"
                  onClick={saveWhatsApp}
                  disabled={waSaving || !E164_REGEX.test(waNumber) || waNumber === currentPhone}
                  data-testid="whatsapp-save-btn"
                  className="h-10 bg-[#25D366] hover:bg-[#1ea952] text-white rounded-lg"
                >
                  {waSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
                </Button>
                {waEditing && (
                  <button
                    type="button"
                    onClick={() => { setWaEditing(false); setWaNumber(currentPhone); }}
                    className="text-xs text-[#64748b] hover:text-[#0f172a] px-2"
                    data-testid="whatsapp-cancel-btn"
                  >
                    Cancel
                  </button>
                )}
              </div>
            )}

            {!isWhatsAppVerified && !waEditing && waNumber && !E164_REGEX.test(waNumber) && (
              <p className="text-[11px] text-red-500 mt-1" data-testid="whatsapp-error">
                Invalid format — must be E.164, e.g. +919812345678
              </p>
            )}
            <p className="text-[11px] text-[#94a3b8] mt-1.5">
              Use international E.164 format (start with country code + digits, no spacing).
              {!isWhatsAppVerified && currentPhone && (
                <span className="ml-1 text-amber-600">Verification pending — a one-time code will be sent when we activate WhatsApp messaging.</span>
              )}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* GST Registration & Business Profile */}
      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="font-outfit text-base font-semibold text-[#0f172a] flex items-center gap-2">
            <Shield className="h-4 w-4 text-[#4338ca]" /> GST & Business Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm font-medium mb-1.5 block">Business Sector</Label>
            <Select value={form.business_sector} onValueChange={(v) => update("business_sector", v)}>
              <SelectTrigger data-testid="settings-business-sector" className="h-10 border-[#e2e8f0]">
                <SelectValue placeholder="Select sector">{form.business_sector || "Select sector"}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {["Solar & Renewable Energy","Education & Training","Gems & Jewellery","Professional Services","General Trading","Construction","Marketing & Advertising","Restaurant & Food Services","Other"].map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-medium mb-1.5 block">GST Registration Status *</Label>
            <Select value={form.gst_status} onValueChange={(v) => { update("gst_status", v); update("is_gst_registered", v !== "unregistered"); }}>
              <SelectTrigger data-testid="settings-gst-status" className="h-10 border-[#e2e8f0]">
                <SelectValue>
                  {form.gst_status === "registered_regular" ? "Registered — Regular" :
                   form.gst_status === "registered_composition" ? "Registered — Composition" : "Unregistered / Exempt"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="registered_regular">Registered — Regular</SelectItem>
                <SelectItem value="registered_composition">Registered — Composition Scheme</SelectItem>
                <SelectItem value="unregistered">Unregistered / Exempt</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-[#64748b] mt-1.5">
              {form.gst_status === "registered_regular" && "Tax Invoices with full GST breakup (CGST/SGST/IGST)"}
              {form.gst_status === "registered_composition" && "Bill of Supply — tax not collected from customers, internal liability tracked"}
              {form.gst_status === "unregistered" && "Commercial Invoices without GST fields. Turnover Tracker enabled."}
            </p>
          </div>
          {form.gst_status !== "unregistered" && (
            <div>
              <Label className="text-sm font-medium mb-1.5 block">GSTIN (15 characters) *</Label>
              <Input value={form.gstin} onChange={(e) => update("gstin", e.target.value.toUpperCase())} maxLength={15}
                placeholder="29AABCT1234F1ZP" data-testid="settings-gstin" className="h-10 border-[#e2e8f0] font-mono tracking-wide" />
              {form.gstin && form.gstin.length > 0 && form.gstin.length !== 15 && (
                <p className="text-xs text-amber-600 mt-1">{form.gstin.length}/15 characters</p>
              )}
            </div>
          )}
          {form.gst_status === "registered_composition" && (
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Composition Category *</Label>
              <Select value={form.composition_category} onValueChange={(v) => update("composition_category", v)}>
                <SelectTrigger data-testid="settings-composition-category" className="h-10 border-[#e2e8f0]">
                  <SelectValue placeholder="Select category">
                    {form.composition_category === "manufacturers_traders" ? "Manufacturers/Traders — 1%" :
                     form.composition_category === "restaurants" ? "Restaurants — 5%" :
                     form.composition_category === "service_providers" ? "Service Providers — 6%" : "Select category"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manufacturers_traders">Manufacturers / Traders — 1% (0.5% CGST + 0.5% SGST)</SelectItem>
                  <SelectItem value="restaurants">Restaurants (No Alcohol) — 5% (2.5% CGST + 2.5% SGST)</SelectItem>
                  <SelectItem value="service_providers">Service Providers — 6% (3% CGST + 3% SGST)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bank Details */}
      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="font-outfit text-base font-semibold text-[#0f172a] flex items-center gap-2">
            <Landmark className="h-4 w-4 text-[#4338ca]" /> Bank Details (Optional)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Bank Name</Label>
              <Input value={form.bank_name} onChange={(e) => update("bank_name", e.target.value)}
                placeholder="HDFC Bank" data-testid="settings-bank-name" className="h-10 border-[#e2e8f0]" />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Account Number</Label>
              <Input value={form.bank_account} onChange={(e) => update("bank_account", e.target.value)}
                placeholder="123456789012" data-testid="settings-bank-account" className="h-10 border-[#e2e8f0] font-mono" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">IFSC Code</Label>
              <Input value={form.bank_ifsc} onChange={(e) => update("bank_ifsc", e.target.value.toUpperCase())}
                placeholder="HDFC0001234" data-testid="settings-bank-ifsc" className="h-10 border-[#e2e8f0] font-mono" />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Branch</Label>
              <Input value={form.bank_branch} onChange={(e) => update("bank_branch", e.target.value)}
                placeholder="Andheri West" data-testid="settings-bank-branch" className="h-10 border-[#e2e8f0]" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export / Foreign Trade (only for GST registered regular users) */}
      {form.gst_status === "registered_regular" && (
        <Card className="bg-white border-[#e2e8f0] shadow-sm" data-testid="export-trade-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-outfit text-base font-semibold text-[#0f172a] flex items-center gap-2">
              <Globe className="h-4 w-4 text-[#4338ca]" /> Export / Foreign Trade
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-[#64748b]">Configure LUT details and export preferences for zero-rated or IGST-paid export invoices.</p>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Default Export Type</Label>
              <Select value={form.export_type_preference || "none"} onValueChange={(v) => update("export_type_preference", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="settings-export-type" className="h-10 border-[#e2e8f0]">
                  <SelectValue>
                    {form.export_type_preference === "lut" ? "Supply under LUT (Zero Tax)" :
                     form.export_type_preference === "igst" ? "Supply with Payment of IGST" : "Not configured"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not configured</SelectItem>
                  <SelectItem value="lut">Supply under LUT (Zero Tax)</SelectItem>
                  <SelectItem value="igst">Supply with Payment of IGST (Refundable)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.export_type_preference === "lut" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">LUT Number</Label>
                  <Input value={form.lut_number} onChange={(e) => update("lut_number", e.target.value)}
                    placeholder="AD270826000012G" data-testid="settings-lut-number" className="h-10 border-[#e2e8f0] font-mono" />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">LUT Valid From</Label>
                  <Input type="date" value={form.lut_date} onChange={(e) => update("lut_date", e.target.value)}
                    data-testid="settings-lut-date" className="h-10 border-[#e2e8f0]" />
                  <p className="text-xs text-[#94a3b8] mt-1">Valid for FY 2026-27</p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">AD Code (Authorized Dealer)</Label>
                <Input value={form.ad_code} onChange={(e) => update("ad_code", e.target.value)}
                  placeholder="e.g., 0230045" data-testid="settings-ad-code" className="h-10 border-[#e2e8f0] font-mono" />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Refund Bank Account</Label>
                <Input value={form.refund_bank_account} onChange={(e) => update("refund_bank_account", e.target.value)}
                  placeholder="A/c for IGST refunds" data-testid="settings-refund-bank" className="h-10 border-[#e2e8f0] font-mono" />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Default Export Currency</Label>
                <Select value={form.default_export_currency || "none"} onValueChange={(v) => update("default_export_currency", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="settings-export-currency" className="h-10 border-[#e2e8f0]">
                    <SelectValue>{form.default_export_currency ? `${form.default_export_currency}` : "Select currency"}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not set</SelectItem>
                    {currencies.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.code} — {c.name} ({c.symbol})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Signatory & Template */}
      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="font-outfit text-base font-semibold text-[#0f172a] flex items-center gap-2">
            <PenTool className="h-4 w-4 text-[#4338ca]" /> Invoice Preferences
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm font-medium mb-1.5 block">Authorized Signatory Name</Label>
            <Input value={form.signatory_name} onChange={(e) => update("signatory_name", e.target.value)}
              placeholder="Raj Kumar" data-testid="settings-signatory" className="h-10 border-[#e2e8f0]" />
          </div>
          <div>
            <Label className="text-sm font-medium mb-2 block flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5" /> Invoice Template
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => update("invoice_template", "professional")}
                data-testid="template-professional"
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  form.invoice_template === "professional"
                    ? "border-[#4338ca] bg-[#4338ca]/5"
                    : "border-[#e2e8f0] hover:border-[#94a3b8]"
                }`}
              >
                <p className="text-sm font-semibold text-[#0f172a]">The Professional</p>
                <p className="text-xs text-[#64748b] mt-0.5">Modern/Clean, blue accents</p>
              </button>
              <button
                onClick={() => update("invoice_template", "classic")}
                data-testid="template-classic"
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  form.invoice_template === "classic"
                    ? "border-[#4338ca] bg-[#4338ca]/5"
                    : "border-[#e2e8f0] hover:border-[#94a3b8]"
                }`}
              >
                <p className="text-sm font-semibold text-[#0f172a]">The Classic</p>
                <p className="text-xs text-[#64748b] mt-0.5">Tally-style, bordered, dense</p>
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Business Logo</Label>
              <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={() => handleImageUpload("logo_base64", logoRef)} />
              <Button variant="outline" size="sm" onClick={() => logoRef.current?.click()} data-testid="upload-logo-btn"
                className="w-full h-10 border-dashed border-[#e2e8f0] text-[#64748b] hover:text-[#4338ca]">
                <Upload className="h-3.5 w-3.5 mr-1.5" /> {form.logo_base64 ? "Change Logo" : "Upload Logo"}
              </Button>
              {form.logo_base64 && <p className="text-xs text-[#10b981] mt-1">Logo uploaded</p>}
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Digital Signature</Label>
              <input ref={sigRef} type="file" accept="image/*" className="hidden" onChange={() => handleImageUpload("signature_base64", sigRef)} />
              <Button variant="outline" size="sm" onClick={() => sigRef.current?.click()} data-testid="upload-signature-btn"
                className="w-full h-10 border-dashed border-[#e2e8f0] text-[#64748b] hover:text-[#4338ca]">
                <Upload className="h-3.5 w-3.5 mr-1.5" /> {form.signature_base64 ? "Change Signature" : "Upload Signature"}
              </Button>
              {form.signature_base64 && <p className="text-xs text-[#10b981] mt-1">Signature uploaded</p>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Google Drive Compliance Vault */}
      <Card className="bg-white border-[#e2e8f0] shadow-sm" data-testid="drive-vault-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-outfit text-base font-semibold text-[#0f172a] flex items-center gap-2">
            <HardDrive className="h-4 w-4 text-[#4338ca]" /> Google Drive Compliance Vault
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-[#64748b]">
            Auto-sync invoices and receipt scans to a structured Google Drive folder: <code className="text-xs bg-[#f1f5f9] px-1.5 py-0.5 rounded font-mono">BizFlowa / FY_YYYY-YY / Invoices|Expenses/[Category]/[Month]</code>
          </p>

          {driveStatus.connected ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between bg-[#f0fdf4] rounded-lg p-3 border border-[#bbf7d0]">
                <div className="flex items-center gap-2">
                  <div className="h-2.5 w-2.5 rounded-full bg-[#22c55e] animate-pulse" />
                  <div>
                    <p className="text-sm font-medium text-[#166534]">Connected</p>
                    <p className="text-xs text-[#16a34a]">{driveStatus.drive_email}</p>
                  </div>
                </div>
                <span className="text-xs text-[#64748b] bg-white px-2 py-1 rounded-md border border-[#e2e8f0]">
                  {driveStatus.upload_count || 0} files synced
                </span>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={syncDrive}
                  disabled={syncing}
                  data-testid="drive-sync-btn"
                  variant="outline"
                  size="sm"
                  className="flex-1 rounded-lg border-[#e2e8f0] text-[#0f172a] hover:bg-[#f1f5f9]"
                >
                  {syncing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FolderSync className="h-3.5 w-3.5 mr-1.5" />}
                  {syncing ? "Syncing..." : "Sync All to Drive"}
                </Button>
                <Button
                  onClick={disconnectDrive}
                  disabled={driveLoading}
                  data-testid="drive-disconnect-btn"
                  variant="outline"
                  size="sm"
                  className="rounded-lg border-red-200 text-red-600 hover:bg-red-50"
                >
                  <Unplug className="h-3.5 w-3.5 mr-1.5" />
                  Disconnect
                </Button>
              </div>
            </div>
          ) : (
            <Button
              onClick={connectDrive}
              disabled={driveLoading}
              data-testid="drive-connect-btn"
              className="bg-[#4338ca] hover:bg-[#3730a3] text-white rounded-xl h-10 px-6 shadow-md shadow-[#4338ca]/15"
            >
              {driveLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : (
                <svg className="h-4 w-4 mr-2" viewBox="0 0 87.3 78.4">
                  <path fill="#0066DA" d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H1.5c0 1.55.4 3.1 1.2 4.5l3.9 9.35z"/>
                  <path fill="#00AC47" d="M43.65 25.15L29.9 1.35C28.55 2.15 27.4 3.25 26.6 4.65L1.5 48.2c-.8 1.4-1.2 2.95-1.2 4.5h27.6l15.75-27.55z"/>
                  <path fill="#EA4335" d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H59.7L73.55 76.8z"/>
                  <path fill="#00832D" d="M43.65 25.15L57.4 1.35C56.05.55 54.5.1 52.9.1H34.4c-1.6 0-3.15.5-4.5 1.25l13.75 23.8z"/>
                  <path fill="#2684FC" d="M59.7 53H27.5L13.75 76.8c1.35.8 2.9 1.25 4.5 1.25h36.75c1.6 0 3.15-.45 4.5-1.25L59.7 53z"/>
                  <path fill="#FFBA00" d="M73.35 26.5l-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3L43.6 25.15 59.7 53h27.6c0-1.55-.4-3.1-1.2-4.5L73.35 26.5z"/>
                </svg>
              )}
              Connect Google Drive
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn"
          className="bg-[#4338ca] hover:bg-[#3730a3] text-white rounded-xl px-8 h-11 shadow-lg shadow-[#4338ca]/20">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : saved ? <Check className="h-4 w-4 mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          {saving ? "Saving..." : saved ? "Saved" : "Save Settings"}
        </Button>
      </div>
    </div>
  );
}
